import React, { useState, useEffect } from "react";
import { ArrowDown, ArrowUp, Phone, PhoneDisconnect, Clock, ChartBar, Target, UserCircle } from "@phosphor-icons/react";
import API from "../services/api";
import "./Dashboard.css";

const fmt = (s) => { if (!s) return "0s"; const m = Math.floor(s/60); return m > 0 ? `${m}m ${s%60}s` : `${s}s`; };
const pct = (a, b) => b > 0 ? Math.round((a/b)*100) : 0;

export default function Dashboard() {
  const [stats, setStats] = useState(null);
  const [queues, setQueues] = useState([]);
  const [activeCalls, setActiveCalls] = useState([]);
  const [leads, setLeads] = useState([]);
  const [loading, setLoading] = useState(true);
  const [now, setNow] = useState(new Date());

  useEffect(() => {
    const load = async () => {
      try {
        const [s, q, c, l] = await Promise.all([
          API.get("/calls/stats/summary"),
          API.get("/twilio/queues"),
          API.get("/twilio/calls"),
          API.get("/contacts?type=lead"),
        ]);
        setStats(s.data); setQueues(q.data); setActiveCalls(c.data); setLeads(l.data);
      } catch {}
      setLoading(false);
    };
    load();
    const interval = setInterval(load, 10000);
    const clockInterval = setInterval(() => setNow(new Date()), 1000);
    return () => { clearInterval(interval); clearInterval(clockInterval); };
  }, []);

  if (loading) return <div style={{display:"flex",alignItems:"center",justifyContent:"center",height:"100%"}}><span className="spinner"/></div>;

  const answerRate = pct(stats?.today_answered, stats?.today_total);
  const wonLeads = leads.filter(l => l.status === "won").length;
  const hotLeads = leads.filter(l => l.score >= 4).length;
  const followUps = leads.filter(l => l.follow_up_date && new Date(l.follow_up_date) <= new Date(Date.now() + 86400000)).length;
  const pipeline = leads.reduce((sum, l) => sum + (parseFloat(l.lead_value) || 0), 0);

  return (
    <div className="dashboard-page">
      <div className="page-header">
        <div>
          <h1 className="page-title">Dashboard</h1>
          <p className="page-subtitle">{now.toLocaleDateString([], {weekday:"long",month:"long",day:"numeric"})} · {now.toLocaleTimeString([], {hour:"2-digit",minute:"2-digit",second:"2-digit"})}</p>
        </div>
        <div style={{display:"flex",alignItems:"center",gap:8}}>
          {activeCalls.length > 0 && (
            <span style={{display:"flex",alignItems:"center",gap:6,background:"rgba(58,187,144,0.1)",border:"1px solid rgba(58,187,144,0.3)",borderRadius:20,padding:"5px 12px",fontSize:12,color:"var(--color-teal)",fontWeight:600}}>
              <span style={{width:7,height:7,borderRadius:"50%",background:"var(--color-teal)",animation:"pulse 1.5s infinite"}}/>
              {activeCalls.length} live call{activeCalls.length > 1 ? "s" : ""}
            </span>
          )}
        </div>
      </div>

      <div className="dashboard-body">
        {/* Top stats row */}
        <div className="stats-grid" style={{gridTemplateColumns:"repeat(6,1fr)"}}>
          {[
            { label:"Calls Today",  value: stats?.today_total || 0,    sub: `${stats?.today_inbound||0} in · ${stats?.today_outbound||0} out`, color:"var(--color-white)", icon:<Phone size={16}/> },
            { label:"Answered",     value: stats?.today_answered || 0, sub: `${answerRate}% rate`, color:"var(--color-teal)", icon:<Phone size={16} weight="fill"/> },
            { label:"Missed",       value: stats?.today_missed || 0,   sub: "today", color:"var(--color-danger)", icon:<PhoneDisconnect size={16}/> },
            { label:"Avg Duration", value: fmt(stats?.avg_duration),   sub: "7-day avg", color:"var(--color-purple)", icon:<Clock size={16}/> },
            { label:"This Week",    value: stats?.week_total || 0,     sub: `${stats?.week_answered||0} answered`, color:"var(--color-blue)", icon:<ChartBar size={16}/> },
            { label:"In Queue",     value: queues[0]?.currentSize || 0, sub: queues[0]?.averageWaitTime ? `${queues[0].averageWaitTime}s avg wait` : "no wait", color: queues[0]?.currentSize > 0 ? "var(--color-orange)" : "var(--color-white)", icon:<Target size={16}/> },
          ].map(({label,value,sub,color,icon}) => (
            <div key={label} className="stat-card">
              <div style={{display:"flex",alignItems:"center",gap:6,marginBottom:8}}>
                <span style={{color,opacity:0.7}}>{icon}</span>
                <span className="stat-label">{label}</span>
              </div>
              <div className="stat-value" style={{color}}>{value}</div>
              {sub && <div className="stat-sub">{sub}</div>}
            </div>
          ))}
        </div>

        {/* Middle row */}
        <div className="dashboard-grid" style={{gridTemplateColumns:"1fr 1fr 1fr"}}>
          {/* Active Calls */}
          <div className="card">
            <div className="card-header">
              <h3>Active Calls</h3>
              <span className="badge badge-teal">{activeCalls.length} live</span>
            </div>
            {activeCalls.length === 0
              ? <div className="empty-state" style={{padding:"24px 0"}}><p>No active calls.</p></div>
              : activeCalls.map(call => (
                <div key={call.sid} className="active-call-item">
                  <div className={`direction-badge ${call.direction}`}>
                    {call.direction === "inbound" ? <ArrowDown size={13}/> : <ArrowUp size={13}/>}
                  </div>
                  <div className="active-call-info">
                    <div className="active-call-number">{call.direction === "inbound" ? call.from : call.to}</div>
                    <div className="active-call-meta">{call.direction} · in-progress</div>
                  </div>
                  <span style={{width:8,height:8,borderRadius:"50%",background:"var(--color-teal)",flexShrink:0}}/>
                </div>
              ))
            }
          </div>

          {/* Sales Queue */}
          <div className="card">
            <div className="card-header"><h3>Sales Queue</h3></div>
            {queues.length === 0
              ? <div className="empty-state" style={{padding:"24px 0"}}><p>No queue data.</p></div>
              : queues.map(q => (
                <div key={q.sid}>
                  <div style={{display:"flex",justifyContent:"space-between",alignItems:"center",marginBottom:16}}>
                    <div>
                      <div style={{fontSize:32,fontWeight:700,color: q.currentSize > 0 ? "var(--color-orange)" : "var(--color-teal)"}}>{q.currentSize}</div>
                      <div style={{fontSize:12,color:"var(--color-gray)"}}>callers waiting</div>
                    </div>
                    <div style={{textAlign:"right"}}>
                      <div style={{fontSize:32,fontWeight:700,color:"var(--color-white)"}}>{q.averageWaitTime || 0}s</div>
                      <div style={{fontSize:12,color:"var(--color-gray)"}}>avg wait time</div>
                    </div>
                  </div>
                  <div style={{height:4,background:"var(--color-surface)",borderRadius:2,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${Math.min(100,(q.currentSize/10)*100)}%`,background: q.currentSize > 0 ? "var(--color-orange)" : "var(--color-teal)",borderRadius:2,transition:"width 0.5s"}}/>
                  </div>
                </div>
              ))
            }
          </div>

          {/* Lead Pipeline */}
          <div className="card">
            <div className="card-header"><h3>Lead Pipeline</h3></div>
            <div style={{display:"flex",flexDirection:"column",gap:12}}>
              {[
                {label:"Total Leads",  value: leads.length,  color:"var(--color-white)"},
                {label:"Hot Leads",    value: hotLeads,      color:"var(--color-orange)"},
                {label:"Won",          value: wonLeads,      color:"var(--color-teal)"},
                {label:"Follow-ups Today", value: followUps, color:"var(--color-blue)"},
                {label:"Pipeline Value", value: `$${pipeline.toLocaleString()}`, color:"var(--color-purple)"},
              ].map(({label,value,color}) => (
                <div key={label} style={{display:"flex",justifyContent:"space-between",alignItems:"center",padding:"6px 0",borderBottom:"1px solid var(--color-surface)"}}>
                  <span style={{fontSize:13,color:"var(--color-gray)"}}>{label}</span>
                  <span style={{fontSize:15,fontWeight:700,color}}>{value}</span>
                </div>
              ))}
            </div>
          </div>
        </div>

        {/* Bottom row - Lead status breakdown */}
        <div className="card" style={{marginTop:0}}>
          <div className="card-header"><h3>Lead Status Breakdown</h3></div>
          <div style={{display:"flex",gap:8,flexWrap:"wrap"}}>
            {[
              {label:"New",         value:"new",         color:"#6b7280"},
              {label:"Contacted",   value:"contacted",   color:"#3b82f6"},
              {label:"Qualified",   value:"qualified",   color:"#8b5cf6"},
              {label:"Proposal",    value:"proposal",    color:"#f59e0b"},
              {label:"Negotiation", value:"negotiation", color:"#f97316"},
              {label:"Won",         value:"won",         color:"#10b981"},
              {label:"Lost",        value:"lost",        color:"#ef4444"},
            ].map(s => {
              const count = leads.filter(l => (l.status||"new") === s.value).length;
              const pctVal = pct(count, leads.length);
              return (
                <div key={s.value} style={{flex:1,minWidth:80,background:"var(--color-surface)",borderRadius:8,padding:"12px",border:`1px solid ${s.color}33`}}>
                  <div style={{fontSize:11,color:s.color,fontWeight:600,textTransform:"uppercase",letterSpacing:0.5,marginBottom:6}}>{s.label}</div>
                  <div style={{fontSize:24,fontWeight:700,color:"var(--color-white)"}}>{count}</div>
                  <div style={{fontSize:11,color:"var(--color-gray)",marginTop:2}}>{pctVal}%</div>
                  <div style={{height:3,background:"var(--color-surface-mid)",borderRadius:2,marginTop:8,overflow:"hidden"}}>
                    <div style={{height:"100%",width:`${pctVal}%`,background:s.color,borderRadius:2}}/>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </div>
    </div>
  );
}
